import * as React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    MenuItem,
    TextField,
} from "@mui/material";
import {LoadingButton} from "@mui/lab";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers-pro";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {Formik, Form} from "formik";
import * as Yup from "yup";
import {dateToString} from "../../Utils/dateUtils";

const validationSchema = Yup.object({
    team_id: Yup.mixed().required("Team is required"),
    start_date: Yup.date()
        .nullable()
        .typeError("Invalid date")
        .required("Start date is required"),
    end_date: Yup.date()
        .nullable()
        .typeError("Invalid date")
        .required("End date is required")
        .min(Yup.ref("start_date"), "End date cannot be earlier than start date"),
});

/**
 * Modal to add a team transition manually for a user.
 * Both Start Date and End Date are required — manual add always creates a
 * closed (past) historical period. The user's current team is only ever
 * changed via the Team field, or by editing an existing transition.
 */
const AddTeamTransitionModal = (props) => {
    const {open, onClose, teamsList, onSubmit} = props;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Add Team Transition</DialogTitle>
            <Formik
                initialValues={{team_id: "", start_date: null, end_date: null}}
                validationSchema={validationSchema}
                onSubmit={(values, {setSubmitting, resetForm}) => {
                    const payload = {
                        team_id: values.team_id,
                        start_date: dateToString(new Date(values.start_date), "yyyy-MM-dd"),
                        end_date: dateToString(new Date(values.end_date), "yyyy-MM-dd"),
                    };

                    onSubmit(payload);
                    setSubmitting(false);
                    resetForm();
                    onClose();
                }}
            >
                {({values, errors, touched, isSubmitting, setFieldValue, handleChange}) => (
                    <Form>
                        <DialogContent>
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        name="team_id"
                                        label="Team"
                                        value={values.team_id}
                                        onChange={handleChange}
                                        error={touched.team_id && Boolean(errors.team_id)}
                                        helperText={touched.team_id && errors.team_id}
                                    >
                                        {teamsList && teamsList.length > 0 ? (
                                            teamsList.map((option) => (
                                                <MenuItem key={option.id} value={option.id} disabled={option.disabled}>
                                                    {option.name}
                                                </MenuItem>
                                            ))
                                        ) : (
                                            <MenuItem disabled>No teams available</MenuItem>
                                        )}
                                    </TextField>
                                </Grid>
                                <Grid item xs={6}>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker
                                            label="Start Date*"
                                            inputFormat="dd/MM/yyyy"
                                            value={values.start_date}
                                            onChange={(newValue) => setFieldValue("start_date", newValue)}
                                            renderInput={(startProps) => (
                                                <TextField
                                                    {...startProps}
                                                    fullWidth
                                                    size="small"
                                                    error={touched.start_date && Boolean(errors.start_date)}
                                                    helperText={touched.start_date && errors.start_date}
                                                />
                                            )}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                                <Grid item xs={6}>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker
                                            label="End Date*"
                                            inputFormat="dd/MM/yyyy"
                                            value={values.end_date}
                                            onChange={(newValue) => setFieldValue("end_date", newValue)}
                                            renderInput={(endProps) => (
                                                <TextField
                                                    {...endProps}
                                                    fullWidth
                                                    size="small"
                                                    error={touched.end_date && Boolean(errors.end_date)}
                                                    helperText={touched.end_date && errors.end_date}
                                                />
                                            )}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions sx={{padding: "16px 24px"}}>
                            <Button
                                type="button"
                                variant="contained"
                                color="secondary"
                                onClick={onClose}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                            <LoadingButton
                                type="submit"
                                variant="contained"
                                color="primary"
                                loading={isSubmitting}
                            >
                                Save Transition
                            </LoadingButton>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
};

export default AddTeamTransitionModal;
